/**
 * Banco local (PLAN §12).
 *
 * Regras que moldam este schema:
 *
 * 1. O catálogo (`categories`, `signs`) é **derivado** — pode ser reconstruído a
 *    partir do catalog.json a qualquer momento.
 * 2. O estado pessoal (`localSignState`, `settings`, `studySessions`,
 *    `downloadQueue`) é **insubstituível** — não existe em lugar nenhum além deste
 *    dispositivo (não há conta nem sincronização no MVP).
 *
 * Por isso as duas coisas ficam em tabelas separadas, e a sincronização só toca no
 * grupo 1. Migrations são sempre explícitas: nada de recriar o banco para "resolver"
 * uma mudança de schema (PLAN §37).
 */
import Dexie, { type EntityTable } from 'dexie';
import type { Category, LocalSignState, Sign } from '@/types/catalog';

/** Metadados da cópia local do catálogo. Linha única, `id = 'current'`. */
export interface CatalogMetadata {
  id: 'current';
  schemaVersion: number;
  catalogVersion: string;
  contentHash: string;
  generatedAt: string;
  sourceUrl: string;
  /** Quando a PWA aplicou este catálogo localmente. */
  appliedAt: string;
  /** Última vez que houve checagem de atualização, com ou sem mudança. */
  lastCheckedAt?: string;
}

export type DownloadJobStatus = 'queued' | 'downloading' | 'paused' | 'error' | 'done';

/**
 * Item da fila de downloads.
 *
 * A fila é persistida em vez de viver em memória porque no iOS a PWA é suspensa a
 * qualquer momento; fechar e reabrir o app não pode perder o que faltava baixar
 * (PLAN §18, critérios de aceite).
 */
export interface DownloadJob {
  id: string;
  signId: string;
  status: DownloadJobStatus;
  /** Ordem de entrada; mantém FIFO estável entre sessões. */
  queuedAt: string;
  startedAt?: string;
  finishedAt?: string;
  bytesDownloaded: number;
  totalBytes?: number;
  attempts: number;
  lastError?: string;
  /**
   * Momento a partir do qual o job pode ser tentado de novo.
   *
   * É o que dá efeito real ao backoff: sem isso, um job devolvido à fila seria
   * reprocessado no mesmo instante e o intervalo entre tentativas viraria enfeite.
   */
  retryAfter?: string;
  /** Categoria que originou o job, quando veio de "baixar categoria". */
  originCategoryId?: string;
}

export interface StudySession {
  id: string;
  startedAt: string;
  finishedAt?: string;
  categoryIds: string[];
  onlyFavorites: boolean;
  onlyOffline: boolean;
  total: number;
  correct: number;
  incorrect: number;
}

/** Preferências. Chave-valor para não exigir migration a cada opção nova. */
export interface Setting {
  key: string;
  value: unknown;
}

export class CcbLibrasDatabase extends Dexie {
  // --- derivado do catálogo ---
  categories!: EntityTable<Category, 'id'>;
  signs!: EntityTable<Sign, 'id'>;
  catalogMetadata!: EntityTable<CatalogMetadata, 'id'>;

  // --- pessoal, nunca sobrescrito pela sincronização ---
  localSignState!: EntityTable<LocalSignState, 'signId'>;
  downloadQueue!: EntityTable<DownloadJob, 'id'>;
  studySessions!: EntityTable<StudySession, 'id'>;
  settings!: EntityTable<Setting, 'key'>;

  constructor(name = 'ccb-libras') {
    super(name);

    // v1 — schema inicial.
    //
    // Índices escolhidos pelas consultas reais da UI:
    //  - signs por categoria e ordem: tela de categoria e navegação anterior/próximo;
    //  - signs por normalizedName: busca por prefixo sem varrer a tabela;
    //  - localSignState por offline.status: tela de downloads e verificação offline;
    //  - localSignState por lastViewedAt: tela de recentes.
    //
    // Campos booleanos (`favorite`, `learned`, `active`) NÃO aparecem aqui: o
    // IndexedDB não aceita boolean como chave de índice — declarar o índice não dá
    // erro, ele simplesmente nunca casa. Essas consultas usam varredura filtrada,
    // o que é irrelevante nesta escala (centenas de registros, não milhões).
    this.version(1).stores({
      categories: 'id, slug, order',
      signs: 'id, slug, categoryId, normalizedName, [categoryId+order]',
      catalogMetadata: 'id',
      localSignState: 'signId, offline.status, lastViewedAt',
      downloadQueue: 'id, signId, status, queuedAt',
      studySessions: 'id, startedAt',
      settings: 'key',
    });
  }
}

/**
 * Instância única.
 *
 * Exportada como `let` para que os testes possam substituir por um banco isolado;
 * a aplicação nunca reatribui.
 */
export let db = new CcbLibrasDatabase();

/** Apenas para testes: troca a instância por uma com outro nome. */
export function __setDatabaseForTests(instance: CcbLibrasDatabase): void {
  db = instance;
}
